import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Smile, Activity, Shield, Heart, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import FloatingCards from '../components/FloatingCards';
import TrustIndicators from '../components/TrustIndicators';
import ContactForm from '../components/ContactForm';
import BookingModal from '../components/BookingModal';
import SchemaMarkup from '../components/SchemaMarkup';
import YouTubeShortsSection from '../components/YouTubeShortsSection';
import GoogleReviewsSection from '../components/GoogleReviewsSection';
import { homePageData, clinicInfo } from '../data/servicesData';

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const services = [
    { icon: Smile, name: 'Clear Aligners', path: '/clear-aligners-east-tambaram' },
    { icon: Activity, name: 'Dental Implants', path: '/dental-implants-chennai' },
    { icon: Shield, name: 'Root Canal', path: '/root-canal-treatment-chennai' },
    { icon: Heart, name: 'Kids Dentistry', path: '/dental-checkup-chennai' },
    { icon: Zap, name: 'Laser Care', path: '/teeth-cleaning-tambaram' }
  ];

  const features = [
    { title: 'Advanced Technology', desc: 'Latest dental equipment and techniques' },
    { title: 'Experienced Doctors', desc: '10+ years of expertise in dentistry' },
    { title: 'Pain-Free Treatment', desc: 'Gentle procedures with modern anesthesia' },
    { title: 'Affordable Pricing', desc: 'Quality care at competitive rates' },
    { title: 'Emergency Care', desc: '24/7 emergency dental services' },
    { title: 'Free Consultation', desc: 'Complimentary first visit assessment' }
  ];

  const testimonials = [
    {
      name: 'Priya Rajan',
      text: 'Best dental clinic in East Tambaram! Dr. Thamarai and team are very professional and caring.',
      rating: 5
    },
    {
      name: 'Kumar Srinivasan',
      text: 'Got my dental implants done here. Painless procedure and excellent results. Highly recommended!',
      rating: 5
    },
    {
      name: 'Lakshmi Narayanan',
      text: 'Clear aligners treatment was amazing. My smile transformation is incredible. Thank you!',
      rating: 5
    }
  ];

  return (
    <div className="homepage">
      <SchemaMarkup />
      
      <section className="hero-section">
        <div className="hero-layout">
          <div className="hero-left">
            <FloatingCards cards={homePageData.floatingCards} />
          </div>
          
          <div className="hero-right">
            <div className="hero-content">
              <h1 className="hero-title">
                Best Dental Clinic in Tambaram
              </h1>
              <h2 className="hero-subtitle">
                Laser Dentistry, Clear Aligners, Implants & Family Dental Care
              </h2>

              <div className="service-icons">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Link to={service.path} key={index} className="service-icon-item">
                      <div className="service-icon-circle">
                        <Icon size={28} />
                      </div>
                      <span>{service.name}</span>
                    </Link>
                  );
                })}
              </div>

              <div className="cta-buttons">
                <a href={`tel:${clinicInfo.phone}`} className="cta-button primary">
                  <Phone size={20} />
                  <span className="btn-text-full">Call {clinicInfo.phone}</span>
                  <span className="btn-text-short">Call Now</span>
                </a>
                <a href="https://wa.me/917502297680" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
                  <span className="btn-text-full">Book Consultation</span>
                  <span className="btn-text-short">Book Now</span>
                  <ArrowRight size={20} />
                </a>
              </div>

              <TrustIndicators />
            </div>
          </div>
        </div>
      </section>

      <YouTubeShortsSection />

      <section className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Why Choose Dr. Thamarai's Dental Care?</h2>
            <p>Your trusted partner for all dental needs in East Tambaram, Chennai</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle className="feature-icon" size={24} />
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="services-overview-section">
        <div className="section-container">
          <div className="section-header">
            <h2>Our Comprehensive Dental Services</h2>
            <p>From routine checkups to advanced treatments</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-card-icon">
                <Smile size={40} />
              </div>
              <h3>Clear Aligners & Invisible Braces</h3>
              <p>Transform your smile with invisible aligners. Free 3D scan included.</p>
              <Link to="/clear-aligners-east-tambaram" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-card-icon">
                <Activity size={40} />
              </div>
              <h3>Dental Implants</h3>
              <p>Permanent tooth replacement with advanced implant technology.</p>
              <Link to="/dental-implants-chennai" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-card-icon">
                <Shield size={40} />
              </div>
              <h3>Root Canal Treatment</h3>
              <p>Pain-free RCT with modern techniques and equipment.</p>
              <Link to="/root-canal-treatment-chennai" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-card-icon">
                <Zap size={40} />
              </div>
              <h3>Laser Dentistry</h3>
              <p>Advanced laser treatments for faster healing and less discomfort.</p>
              <Link to="/teeth-cleaning-tambaram" className="service-link">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <GoogleReviewsSection />

      <BookingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        pageTitle="Home - Dental Services"
      />
    </div>
  );
};

export default HomePage;
